import { FC, useMemo } from "react";
import StarRateNewTrustScore from "../../../components/StarRate2.0";
import { scoreToDisplayStars } from "../../../config/rating";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

const LOGO_MARK = `${process.env.PUBLIC_URL}/images/logos/logo-mark.webp`;

/**
 * Trust Badge (v2) — admin-preview-only React version of the widget
 * actually rendered on customer sites by widget-v2.js (DOM-injection, no
 * iframe). This component is reachable the same way widget-1..14 are
 * (/widgets?templateId=15, opened in an iframe within our own admin panel)
 * purely so it shows up in the "get code" preview — it is NOT what gets
 * embedded on a customer's page. The real embed markup/logic lives in
 * public/assets/bootstrap/widget-v2.js and must be kept visually in sync
 * with this component by hand (no shared source between the two).
 */
const WidgetTemplate15: FC<IWidgetTemplate> = ({ company, score, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("badge", style);
  const payload = useMemo(() => ({ companyId, widgetId: 15 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-badge`,
    [companyId],
  );
  const starsValue = scoreToDisplayStars(score?.score ?? 0);
  const reviewsStr = (score?.totalReview ?? 0).toLocaleString();

  const linkProps = {
    href: profileUrl,
    target: "_blank" as const,
    rel: "noopener",
    onClick: () => ReachClick(payload),
    style: { textDecoration: "none", color: "#1c1c2e" },
  };

  if (resolvedStyle === "compact") {
    return (
      <div className="d-flex justify-content-center">
        <a {...linkProps} className="d-inline-flex align-items-center gap-2" style={{ ...linkProps.style, border: "1px solid #e7e8ec", borderRadius: 999, background: "#fff", padding: "6px 12px", boxShadow: "0 1px 2px rgba(28,28,46,0.05)" }}>
          <b style={{ fontSize: 14, color: "#006bf1" }}>{starsValue}</b>
          <StarRateNewTrustScore value={score?.score} size={11} />
          <span style={{ fontSize: 10.5, color: "#6b6b85" }}>{reviewsStr} reviews</span>
        </a>
      </div>
    );
  }

  if (resolvedStyle === "vertical") {
    return (
      <div className="d-flex justify-content-center">
        <a {...linkProps} className="text-center" style={{ ...linkProps.style, border: "1px solid #e7e8ec", borderRadius: 8, background: "#fff", boxShadow: "0 1px 2px rgba(28,28,46,0.05)", padding: "12px 20px" }}>
          <div className="d-flex justify-content-center mb-1"><img src={LOGO_MARK} alt="TrustFinance" width={22} height={22} /></div>
          <b style={{ display: "block", fontSize: 24, lineHeight: 1.2 }}>{starsValue}</b>
          <div className="d-flex justify-content-center mt-1"><StarRateNewTrustScore value={score?.score} size={11} /></div>
          <div style={{ fontSize: 10, color: "#6b6b85", marginTop: 4 }}>TrustScore {score?.score} · {reviewsStr} reviews</div>
        </a>
      </div>
    );
  }

  if (resolvedStyle === "mono") {
    return (
      <div className="d-flex justify-content-center">
        <a {...linkProps} className="d-inline-flex align-items-center gap-2" style={{ ...linkProps.style, border: "1px solid rgba(28,28,46,0.2)", borderRadius: 8, padding: "8px 12px" }}>
          <b style={{ fontSize: 13 }}>{starsValue} / 5</b>
          <span style={{ fontSize: 11 }}>on TrustFinance · {reviewsStr} reviews</span>
        </a>
      </div>
    );
  }

  if (resolvedStyle === "logo-combo") {
    return (
      <div className="d-flex justify-content-center">
        <a {...linkProps} className="d-flex align-items-center gap-2">
          <img src={LOGO_MARK} alt="TrustFinance" width={20} height={20} />
          <b style={{ fontSize: 12.5, color: "#006bf1" }}>TrustFinance</b>
          <StarRateNewTrustScore value={score?.score} size={12} />
          <span style={{ fontSize: 11.5, color: "#6b6b85" }}><b style={{ color: "#1c1c2e" }}>{starsValue}</b> · {reviewsStr}</span>
        </a>
      </div>
    );
  }

  // standard — WidgetBadge
  return (
    <div className="d-flex justify-content-center">
      <a {...linkProps} className="d-flex align-items-center gap-3" style={{ ...linkProps.style, border: "1px solid #e7e8ec", borderRadius: 8, padding: 14, background: "#fff", boxShadow: "0 1px 2px rgba(28,28,46,0.05)" }}>
        <div className="text-center">
          <b style={{ display: "block", fontSize: 22, color: "#006bf1", lineHeight: 1 }}>{starsValue}</b>
          <div className="mt-1"><StarRateNewTrustScore value={score?.score} size={11} /></div>
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.4, color: "#6b6b85" }}>
          <div>TrustScore {score?.score}</div>
          <div>{reviewsStr} verified reviews</div>
          <div className="d-flex align-items-center gap-1" style={{ marginTop: 2 }}>
            <img src={LOGO_MARK} alt="TrustFinance" width={12} height={12} />
            <b style={{ color: "#006bf1" }}>TrustFinance</b>
          </div>
        </div>
      </a>
    </div>
  );
};

export default WidgetTemplate15;
